import { type Locale } from "date-fns";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  IntlContext,
  type IntlShape,
  createIntl,
  createIntlCache,
} from "react-intl";

import { createTranslationFetcher } from "./utils/fetcher";
import { initLocale } from "./utils/initLocale";
import {
  DEFAULT_LOCALE,
  getLanguageAndLocaleFromUrl,
} from "./utils/localeHelpers";
import { setShowTranslationKey } from "./utils/showTranslationKey";

interface Translation {
  dateFnsLocale?: Locale;
  showKeys: boolean;
}

const TranslationContext = createContext<Translation>(null!);

interface Props {
  children: ReactNode;
}

// Global translations
const cache = createIntlCache();
export let intl: IntlShape | null = null;

export const TranslationProvider = ({ children }: Props) => {
  const [dateFnsLocale, setDateFnsLocale] = useState<Locale | null>(null);
  const locale = getLanguageAndLocaleFromUrl();
  const { country, language } = locale ?? DEFAULT_LOCALE;
  const showKeys = setShowTranslationKey();

  useEffect(() => {
    const localeString = `${language}-${country.toUpperCase()}`;
    const fetcher = createTranslationFetcher(country, language);

    fetcher()
      .then((translations) => {
        const message: Record<string, string> = JSON.parse(translations);
        intl = createIntl(
          {
            locale: localeString,
            messages: message,
          },
          cache
        );
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load translations, using default", err);
        intl = createIntl(
          {
            locale: localeString,
          },
          cache
        );
      })
      .finally(() => {
        setDateFnsLocale(initLocale(localeString));
      });
  }, [country, language]);

  if (!intl) {
    return null;
  }

  return (
    <TranslationContext.Provider
      value={{ dateFnsLocale: dateFnsLocale ?? undefined, showKeys }}
    >
      <IntlContext value={intl}>{children}</IntlContext>
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);
